import { useMutation } from "@apollo/client";
import { MenuItem } from "@szhsin/react-menu";
import { MenuDivider } from "@szhsin/react-menu";
import { SubMenu } from "@szhsin/react-menu";
import { useCallback } from "react";
import {
  AddBotToMeetingNewPageMutationMutation,
  AddBotToMeetingNewPageMutationMutationVariables,
  ChangeBotRecordingStatusNewPageMutationMutation,
  ChangeBotRecordingStatusNewPageMutationMutationVariables,
  PermissionNode,
  RemoveBotFromMeetingNewPageMutationMutation,
  RemoveBotFromMeetingNewPageMutationMutationVariables,
} from "types/graphql-schema";

import deleteMeetingTranscriptsMutation from "@apps/meeting-new/components/prep/graphql/delete-meeting-transcripts-mutation";
import addBotToMeetingMutation from "@apps/meeting-new/graphql/add-bot-to-meeting-mutation";
import changeBotRecordingStatusMutation from "@apps/meeting-new/graphql/change-bot-recording-status-mutation";
import removeBotFromMeetingMutation from "@apps/meeting-new/graphql/remove-bot-from-meeting-mutation";
import { successNotificationVar } from "@cache/cache";
import useConfirm from "@helpers/hooks/use-confirm";
import { titleCase } from "@helpers/string";

const MeetingTranscriptionSubmenu = ({
  meeting,
}: {
  meeting: {
    id: number;
    hasBotInMeeting: boolean;
    botIsRecording: boolean;
    botIsWaitingToJoinMeeting: boolean;
    canUpdate?: Pick<PermissionNode, "permission"> | null;
    transcript?: {
      id: number;
    } | null;
  };
}) => {
  const [addBotToMeeting] = useMutation<
    AddBotToMeetingNewPageMutationMutation,
    AddBotToMeetingNewPageMutationMutationVariables
  >(addBotToMeetingMutation);
  const [removeBotFromMeeting] = useMutation<
    RemoveBotFromMeetingNewPageMutationMutation,
    RemoveBotFromMeetingNewPageMutationMutationVariables
  >(removeBotFromMeetingMutation);
  const [changeBotRecordingStatus] = useMutation<
    ChangeBotRecordingStatusNewPageMutationMutation,
    ChangeBotRecordingStatusNewPageMutationMutationVariables
  >(changeBotRecordingStatusMutation);

  const handleClickAddBotInMeeting = () => {
    addBotToMeeting({
      variables: {
        meetingId: meeting.id,
      },

      optimisticResponse: {
        __typename: "Mutation",
        addBotToMeeting: {
          __typename: "AddBotToMeetingMutation",
          meeting: {
            __typename: "MeetingNode",
            ...meeting,
            hasBotInMeeting: false,
            botIsWaitingToJoinMeeting: true,
          },
        },
      },
      onCompleted: () => {
        successNotificationVar({
          title: "Adding notetaker to meeting.",
          description:
            "This may take a few seconds, and you might have to admit the notetaker in the meeting.",
          timeout: 10000,
        });
      },
    });
  };

  const handleClickRemoveBotInMeeting = () => {
    removeBotFromMeeting({
      variables: {
        meetingId: meeting.id,
      },

      optimisticResponse: {
        __typename: "Mutation",
        removeBotFromMeeting: {
          __typename: "RemoveBotFromMeetingMutation",
          meeting: {
            __typename: "MeetingNode",
            ...meeting,
          },
        },
      },
      onCompleted: () => {
        successNotificationVar({
          title: "Removing notetaker from meeting",
          timeout: 5000,
        });
      },
    });
  };

  const handleClickPauseBotRecording = () => {
    const newValue = !meeting.botIsRecording;
    changeBotRecordingStatus({
      variables: {
        meetingId: meeting.id,
        isRecording: newValue,
      },
      onCompleted: () => {
        successNotificationVar({
          title: newValue ? "Pausing transcription" : "Resuming transcription",
          timeout: 5000,
        });
      },
    });
  };

  const {
    ConfirmationDialog: ConfirmationDeleteTranscript,
    confirm: confirmDeleteTranscript,
  } = useConfirm(
    "Are you sure?",
    "Are you sure you want to delete the transcript?"
  );
  const [deleteMeetingTranscripts] = useMutation(
    deleteMeetingTranscriptsMutation
  );

  const handleClickDeleteTranscript = useCallback(async () => {
    const confirmation = await confirmDeleteTranscript();
    if (confirmation) {
      deleteMeetingTranscripts({
        variables: { meetingId: meeting.id },
      });
    }
  }, [confirmDeleteTranscript, deleteMeetingTranscripts, meeting]);

  return (
    <>
      <ConfirmationDeleteTranscript />
      <SubMenu label="Transcription" className="text-sm">
        <MenuItem className={"text-sm"}>
          <span className="font-bold mr-1">Status:</span>
          <span className="text-gray-500">
            {titleCase(
              meeting.botIsRecording
                ? "Transcribing"
                : meeting.hasBotInMeeting
                ? "Paused"
                : meeting.botIsWaitingToJoinMeeting
                ? "Waiting to Join"
                : "Not Transcribing"
            )}
          </span>
        </MenuItem>
        {meeting.canUpdate?.permission && (
          <>
            {meeting.hasBotInMeeting ? (
              <>
                <MenuDivider />
                <MenuItem
                  className={"text-sm"}
                  onClick={handleClickPauseBotRecording}
                >
                  {meeting.botIsRecording
                    ? "Pause Transcription"
                    : "Resume Transcription"}
                </MenuItem>
                <MenuItem
                  className={"text-sm"}
                  onClick={handleClickRemoveBotInMeeting}
                >
                  Remove Notetaker
                </MenuItem>
              </>
            ) : !meeting.botIsWaitingToJoinMeeting ? (
              <>
                <MenuDivider />
                <MenuItem
                  className={"text-sm"}
                  onClick={handleClickAddBotInMeeting}
                >
                  {titleCase("Add Notetaker To Meeting")}
                </MenuItem>
              </>
            ) : null}

            {meeting?.transcript?.id && (
              <>
                <MenuDivider />
                <MenuItem
                  className={"text-sm"}
                  onClick={handleClickDeleteTranscript}
                >
                  Delete Transcript
                </MenuItem>
              </>
            )}
          </>
        )}
      </SubMenu>
      <MenuDivider />
    </>
  );
};

export default MeetingTranscriptionSubmenu;
